// Quiz data normalizer — bridges THREE distinct schemas used across our lesson JSON files.
//
// Schema A (axel_lessons_1_1_to_1_2.json):
//   { q_id, question, options: ["A. ...", "B. ...", "C. ...", "D. ..."], correct: <0|1|2|3>, explanation }
//
// Schema B (axel-lessons-1.3.1-to-1.4.5.json):
//   { q, options: ["A. ...", "B. ...", "C. ...", "D. ..."], answer: "A" | "B" | ..., explanation }
//
// Schema C (every other axel-lessons-*.json — 1.5 through 5.6, 1290 questions):
//   { q, options: { A: "...", B: "...", C: "...", D: "..." }, answer: "A" | "B" | ..., explanation }
//
// QuizModal in LessonsHub.jsx reads `q.q_id`, `q.question`, `q.correct` and renders
// `q.options.map(...)`. Without normalization Schema B/C silently break:
//   • picks[q.q_id] all collapse to picks[undefined] (the "pick A → all A" bug)
//   • q.correct === idx is always undefined === idx → correct answers shown red
//   • Schema C dict-shape options aren't iterable as an array → no buttons render
//
// This helper produces a unified shape:
//   { q_id, question, options: ["A. ...", "B. ...", ...], correct: <number>, explanation }
const LETTER_TO_INDEX = { A: 0, B: 1, C: 2, D: 3, E: 4, F: 5 };
const LETTERS = ["A", "B", "C", "D", "E", "F"];

function deriveCorrectIndex(q) {
  // Schema A — already numeric.
  if (typeof q.correct === "number" && Number.isFinite(q.correct)) return q.correct;
  // Schema B / C — letter answer.
  const letterRaw = (q.answer ?? q.correct ?? "").toString().trim().toUpperCase();
  if (letterRaw && LETTER_TO_INDEX[letterRaw[0]] != null) return LETTER_TO_INDEX[letterRaw[0]];
  return 0;
}

// Some lesson files store options as a {A:'…', B:'…', …} dict instead of an array.
// Convert to ["A. …", "B. …", …] preserving the canonical letter prefix so the UI
// reads consistently across schemas.
function toOptionsArray(opts) {
  if (Array.isArray(opts)) return opts;
  if (opts && typeof opts === "object") {
    const presentLetters = LETTERS.filter((k) => k in opts);
    const keys = presentLetters.length ? presentLetters : Object.keys(opts);
    return keys.map((k) => {
      const val = String(opts[k] ?? "");
      // Avoid double-prefixing if the value already starts with "A.", "B.", etc.
      const trimmed = val.trim();
      const alreadyPrefixed = /^[A-F][.)]\s/i.test(trimmed);
      return alreadyPrefixed ? trimmed : `${k}. ${trimmed}`;
    });
  }
  return [];
}

export function normalizeQuiz(quiz) {
  if (!Array.isArray(quiz)) return [];
  return quiz.map((raw, i) => ({
    q_id: raw.q_id != null ? raw.q_id : i + 1,
    question: raw.question ?? raw.q ?? "",
    options: toOptionsArray(raw.options),
    correct: deriveCorrectIndex(raw),
    explanation: raw.explanation ?? "",
  }));
}

export function normalizeLesson(lesson) {
  if (!lesson || typeof lesson !== "object") return lesson;
  return { ...lesson, quiz: normalizeQuiz(lesson.quiz) };
}

export function normalizeLessons(list) {
  if (!Array.isArray(list)) return [];
  return list.map(normalizeLesson);
}
